import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  Row,
  ModalHeader,
  Input,
  Label,
  Spinner
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { toast } from "react-toastify";
import axiosInstance from "../../components/AxiosInstance";

const User = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [userData, setUserData] = useState([]);
  const [roles, setRoles] = useState([]);
  const [otpModal, setOtpModal] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    username: "",
    name: "",
    email: "",
    role_id: "",
    company_id: 1,
    password: "",
  });

  const authToken = localStorage.getItem("access");

  document.title = "Users | HVACSYNC";

  /* -------------------- API CALLS -------------------- */

  const fetchRoles = async () => {
    try {
      const res = await axiosInstance({
        url: "/roles/",
        method: "GET",
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setRoles(res.data?.results || []);
    } catch {
      toast.error("Failed to load roles");
    }
  };

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const res = await axiosInstance({
        url: "/users/",
        method: "GET",
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setUserData(res.data?.results || []);
    } catch {
      toast.error("Failed to load users");
    }
    finally{
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  useEffect(() => {
    if (editUser) {
      setFormData({
        username: editUser.username || "",
        name: editUser.name,
        email: editUser.email,
        role_id: editUser.role.id,
        company_id: editUser.company_id || 1,
        password:"",
      });
    } else {
      setFormData({
        username: "",
        name: "",
        email: "",
        role_id: "",
        company_id: 1,
        password: "",
      });
    }
  }, [editUser]);

  /* -------------------- HANDLERS -------------------- */

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const payload = {
      ...formData,
      role_id: Number(formData.role_id),
      company_id: Number(formData.company_id),
    };

     if (!editUser) {
    payload.password = formData.password;
  }
    try {
      await axiosInstance({
        url: editUser ? `https://api.hvacsync.in/users/${editUser.id}/` : "https://api.hvacsync.in/users/",
        method: editUser ? "PATCH" : "POST",
        headers: { Authorization: `Bearer ${authToken}` },
        data: payload,
      });

      if (editUser) {
        toast.success("User updated successfully");
        fetchUsers();
      } else {
        toast.info("OTP sent to email");
        setOtpModal(true);
      }

      setModalOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.message || "Validation error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setVerifyingOtp(true);

    try {
      await axiosInstance({
        url: "https://api.hvacsync.in/auth/verify-otp/",
        method: "POST",
        headers: { Authorization: `Bearer ${authToken}` },
        data: { email: formData.email, otp: otpValue }
      });

      toast.success("User verified successfully");
      setOtpModal(false);
      setOtpValue("");
      fetchUsers();
    } catch {
      toast.error("Invalid OTP");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm("Delete this user?")) return;

    try {
      await axiosInstance({
        url: `/users/${id}/`,
        method: "DELETE",
        headers: { Authorization: `Bearer ${authToken}` }
      });
      toast.success("User deleted");
      fetchUsers();
    } catch {
      toast.error("Failed to delete user");
    }
  };

  /* -------------------- HELPERS -------------------- */

  const filteredUsers = userData.filter(u =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );


  return (
    
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="HVACSYNC" breadcrumbItem="Users" />

        <Card>
          <CardHeader>
            <h4>User Management</h4>
          </CardHeader>
          <CardBody>
            <div className="d-flex justify-content-between mb-3">
              <Button
                color="success"
                onClick={() => { setEditUser(null); setModalOpen(true); }}
              >
                Add User
              </Button>
              <Input
                className="w-25"
                placeholder="Search..."
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>

            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Action</th>
                </tr>
              </thead>
                   {isLoading ? (
                                                    <tbody>
                                                        <tr>
                                                            <td colSpan="8" className="text-center">
                                                                <div className="spinner-border text-primary" role="status">
                                                                    <span className="visually-hidden">Loading...</span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                     ) : (
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.id}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>{user?.role?.name}</td>
                    <td>
                      <Button size="sm" color="warning" className="me-2"
                        onClick={() => { setEditUser(user); setModalOpen(true); }}>
                        Edit
                      </Button>
                      <Button size="sm" color="danger"
                        onClick={() => handleDeleteUser(user.id)}>
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
         )}
            </table>
          </CardBody>
        </Card>
      </Container>

      {/* ADD / EDIT MODAL */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
        <ModalHeader toggle={() => setModalOpen(false)}>
          {editUser ? "Edit User" : "Add User"}
        </ModalHeader>
        <form onSubmit={handleSubmit}>
          <ModalBody>
           
              <div className="mb-3" >
                <Label>Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
            <div className="mb-3" >
                <Label>Email</Label>
                <Input
                  id="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="mb-3" >
                <Label> User Name</Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                />
              </div>

            <div className="mb-3">
              <Label>Role</Label>
              <Input
                type="select"
                id="role_id"
                value={formData.role_id}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Role</option>
                {roles.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </Input>
            </div>

            {!editUser && (
              <div className="mb-3">
                <Label>Password</Label>
                <Input
                  type="password"
                  id="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                />
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button color="success" disabled={isLoading}>
              {isLoading ? <Spinner size="sm" /> : editUser ? "Update" : "Get OTP"}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* OTP MODAL */}
      <Modal isOpen={otpModal} centered>
        <ModalHeader>Verify OTP</ModalHeader>
        <form onSubmit={handleVerifyOtp}>
          <ModalBody>
             <p className="text-muted mb-2">
        OTP has been sent to <strong>{formData.email}</strong>
      </p>
            <Input
              placeholder="Enter OTP"
              value={otpValue}
              onChange={e => setOtpValue(e.target.value)}
              required
            />
          </ModalBody>
          <ModalFooter>
            <Button color="success" disabled={verifyingOtp} className="w-100">
              {verifyingOtp ? <Spinner size="sm" /> : "Verify"}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
};

export default User;
